import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { reconciliationsAPI } from '../services/api';

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const SUPPORTED_FILE = /\.xlsx?$/i;

export default function PayoutReportUpload() {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadedFile, setUploadedFile] = useState('');

  const handleUpload = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setError('');
    setUploadedFile('');
    if (!SUPPORTED_FILE.test(file.name)) {
      setError('Select an HDFC payout report in .xls or .xlsx format.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('The payout report must be 25 MB or smaller.');
      return;
    }

    setUploading(true);
    try {
      const response = await reconciliationsAPI.getUploadUrl(file.name);
      await reconciliationsAPI.uploadToS3(response.data, file);
      setUploadedFile(file.name);
    } catch (uploadError) {
      setError(uploadError?.response?.data?.message || uploadError.message || 'Unable to upload payout report.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 840, mx: 'auto', width: '100%', py: { xs: 0.5, sm: 2 } }}>
      <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center" sx={{ mb: 1.25 }}>
        <Box sx={{
          width: { xs: 42, sm: 52 },
          height: { xs: 42, sm: 52 },
          borderRadius: 2.5,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          boxShadow: 2
        }}>
          <AccountBalanceIcon sx={{ fontSize: { xs: 24, sm: 30 } }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{ fontSize: { xs: '1.55rem', sm: '2rem' }, fontWeight: 700, lineHeight: 1.2 }}
          >
            Payout Report Upload
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.35, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Import HDFC settlement reports for reconciliation
          </Typography>
        </Box>
      </Stack>

      <Typography color="text.secondary" sx={{ mb: { xs: 2.5, sm: 3 }, lineHeight: 1.65, maxWidth: 700 }}>
        Upload a system-wide payout workbook. Transactions will be assigned to buildings using report data and matching payment records.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {uploadedFile && (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          sx={{ mb: 2, borderRadius: 2, alignItems: 'flex-start' }}
          onClose={() => setUploadedFile('')}
        >
          <Typography sx={{ fontWeight: 700, lineHeight: 1.35 }}>Upload complete</Typography>
          <Typography variant="body2" sx={{ mt: 0.35 }}>
            Processing has started. Reconciliation data will appear shortly.
          </Typography>
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            sx={{ mt: 0.75, overflowWrap: 'anywhere' }}
          >
            {uploadedFile}
          </Typography>
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 5 },
          textAlign: 'center',
          border: '1px dashed',
          borderColor: 'primary.light',
          borderRadius: 3,
          bgcolor: 'background.paper',
          backgroundImage: theme => `linear-gradient(180deg, ${theme.palette.primary.main}08, transparent 70%)`
        }}
      >
        <Box sx={{
          width: { xs: 64, sm: 72 },
          height: { xs: 64, sm: 72 },
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'grid',
          placeItems: 'center'
        }}>
          <UploadFileIcon sx={{ fontSize: { xs: 34, sm: 40 } }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Select payout report</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Choose an HDFC report from your device
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3, flexWrap: 'wrap', rowGap: 1 }}>
          <Chip label="XLS" size="small" variant="outlined" />
          <Chip label="XLSX" size="small" variant="outlined" />
          <Chip label="Up to 25 MB" size="small" variant="outlined" />
        </Stack>
        <Button
          variant="contained"
          component="label"
          size="large"
          startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
          disabled={uploading}
          sx={{
            minWidth: { xs: '100%', sm: 220 },
            minHeight: 48,
            borderRadius: 2,
            fontWeight: 700,
            boxShadow: 2
          }}
        >
          {uploading ? 'Uploading…' : 'Choose report'}
          <input
            hidden
            type="file"
            accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleUpload}
          />
        </Button>
      </Paper>
    </Box>
  );
}
